import { useReviewStore } from '@/stores/reviewStore';
import { onBeforeUnmount, ref, watch, type Ref } from 'vue';
import { logger } from '@/utils/logger';

const POLL_INTERVAL_MS = 2000;

export function useReviewPolling(
  reviewId: string,
  onUpdate?: () => void
): {
  isPolling: Ref<boolean>;
  startPolling: () => void;
  stopPolling: () => void;
  checkAndStartPolling: () => void;
} {
  const reviewStore = useReviewStore();
  const isPolling = ref(false);
  const pollingInterval = ref<ReturnType<typeof setInterval> | null>(null);

  const startPolling = (): void => {
    if (isPolling.value) return;

    isPolling.value = true;

    pollingInterval.value = setInterval(async () => {
      try {
        const currentReview = await reviewStore.getReviewByUid(reviewId);

        if (!currentReview) {
          stopPolling();
          return;
        }

        const totalCount = currentReview.total_count ?? 0;
        const finishedCount = currentReview.finished_count ?? 0;

        if (totalCount > 0 && finishedCount >= totalCount) {
          if (onUpdate) onUpdate();
          stopPolling();
          return;
        }

        if (onUpdate && finishedCount > 0) onUpdate();
      } catch (error) {
        logger.error('[ReviewPolling] Error during polling:', error);
      }
    }, POLL_INTERVAL_MS);
  };

  const stopPolling = (): void => {
    if (pollingInterval.value) {
      clearInterval(pollingInterval.value);
      pollingInterval.value = null;
    }
    isPolling.value = false;
  };

  const checkAndStartPolling = (): void => {
    const currentReview = reviewStore.current;

    if (!currentReview || currentReview.uid !== reviewId) {
      return;
    }

    if (
      (currentReview.total_count ?? 0) > 0 &&
      (currentReview.finished_count ?? 0) < (currentReview.total_count ?? 0)
    ) {
      startPolling();
    }
  };

  watch(
    () => reviewStore.current,
    newReview => {
      if (!newReview || newReview.uid !== reviewId) {
        stopPolling();
        return;
      }

      if (
        (newReview.total_count ?? 0) > 0 &&
        (newReview.finished_count ?? 0) < (newReview.total_count ?? 0)
      ) {
        if (!isPolling.value) {
          startPolling();
        }
      } else {
        stopPolling();
      }
    },
    { immediate: true }
  );

  onBeforeUnmount(() => {
    stopPolling();
  });

  return {
    isPolling,
    startPolling,
    stopPolling,
    checkAndStartPolling,
  };
}
